import type { ReactNode } from 'react';
import { Spinner } from '@/shared/components/ui/Spinner';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { section3StatusFromError, section3StatusLabel } from './section3Status';
import { Section5PrereqNotice } from './Section5PrereqNotice';

interface Section3StateViewProps {
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  isEmpty?: boolean;
  isFetching?: boolean;
  prereqTitle: string;
  prereqDescription: string;
  emptyTitle?: string;
  emptyDescription?: string;
  children: ReactNode;
}

/**
 * Shared 24.6 state machine for Section 5 panels. An unavailable or hidden
 * resource surfaces as 404 → an honest notice (never fabricated data);
 * 403 → forbidden; 5xx/timeout → retryable. Text always accompanies state.
 */
export function Section3StateView(props: Section3StateViewProps) {
  const { isLoading, isError, error, isEmpty, isFetching, prereqTitle, prereqDescription, emptyTitle, emptyDescription, children } = props;

  if (isLoading) {
    return (
      <div className="flex h-40 items-center justify-center" role="status" aria-label="Loading">
        <Spinner />
      </div>
    );
  }

  if (isError) {
    const status = section3StatusFromError(error);
    if (status === 'not-found') {
      return <Section5PrereqNotice title={prereqTitle} description={prereqDescription} />;
    }
    return (
      <EmptyState
        title={section3StatusLabel(status)}
        description={
          status === 'forbidden'
            ? 'You are not authorized for this workflow. Controls stay hidden; the server enforces access.'
            : status === 'retryable'
              ? 'The service is temporarily unavailable. Retry shortly.'
              : 'This section could not be loaded.'
        }
      />
    );
  }

  if (isEmpty) {
    return <EmptyState title={emptyTitle ?? 'Nothing to show yet'} description={emptyDescription ?? 'Data will appear here once available.'} />;
  }

  return (
    <div aria-busy={isFetching ? true : undefined} className={isFetching ? 'opacity-70 transition-opacity' : undefined}>
      {children}
    </div>
  );
}
